
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.handleOverlayClick = this.close.bind(this);

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    this.querySelector('#CartDrawer-Overlay')
      ?.addEventListener('click', this.handleOverlayClick);

    this.setHeaderCartIconAccessibility();

    // WHYNTER FIX:
    // Keep the empty-cart state synchronized with
    // the products actually displayed in the drawer.
    // This also handles custom AJAX cart updates.
    this.cartItemsObserver = new MutationObserver(() => {
      this.syncCartEmptyState();
    });

    this.cartItemsObserver.observe(this, {
      childList: true,
      subtree: true
    });

    this.syncCartEmptyState();
  }

  syncCartEmptyState() {
    const cartItems = this.querySelector('#CartDrawer-CartItems');

    if (!cartItems) return;

    const hasProducts = !!cartItems.querySelector('.cart-item');

    // Remove stale is-empty classes when products exist.
    this.classList.toggle('is-empty', !hasProducts);

    const drawerInner = this.querySelector('.drawer__inner');

    if (drawerInner) {
      drawerInner.classList.toggle('is-empty', !hasProducts);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    // Ensure the correct cart state before opening.
    this.syncCartEmptyState();

    const cartDrawerNote = this.querySelector(
      '[id^="Details-"] summary'
    );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    // Preserve the original Dawn opening animation.
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');

    removeTrapFocus(this.activeElement);

    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    const nextElement = cartDrawerNote.nextElementSibling;

    if (nextElement && nextElement.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        nextElement.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener(
      'keyup',
      onKeyUpEscape
    );
  }

  renderContents(parsedState) {
    // WHYNTER FIX:
    // Validate Shopify's AJAX response before
    // attempting to update the drawer.
    if (
      !parsedState ||
      !parsedState.sections ||
      !parsedState.sections['cart-drawer']
    ) {
      console.error(
        'Whynter Cart Drawer: Missing cart section data.',
        parsedState
      );
      return;
    }

    this.productId = parsedState.id;

    // Read the empty-cart state from Shopify's
    // freshly rendered cart section.
    const updatedDrawer = this.getSectionDOM(
      parsedState.sections['cart-drawer'],
      'cart-drawer'
    );

    if (updatedDrawer) {
      const isEmpty = updatedDrawer.classList.contains(
        'is-empty'
      );

      this.classList.toggle('is-empty', isEmpty);
    }

    // Update the product list and cart icon.
    this.getSectionsToRender().forEach((section) => {
      const sectionHTML = parsedState.sections[section.id];

      if (!sectionHTML) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const updatedSection = this.getSectionDOM(
        sectionHTML,
        section.selector || '.shopify-section'
      );

      if (!updatedSection) {
        console.error(
          'Whynter Cart Drawer: Could not locate section:',
          section.id
        );
        return;
      }

      sectionElement.innerHTML = updatedSection.innerHTML;
    });

    // WHYNTER FIX:
    // Synchronize the empty state AFTER replacing
    // the cart HTML, so CSS cannot hide new products.
    this.syncCartEmptyState();

    // Restore overlay functionality and open the drawer.
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')
        ?.addEventListener('click', this.handleOverlayClick);

      this.open();
    });
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    const element = this.getSectionDOM(html, selector);

    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    if (!html || typeof html !== 'string') {
      return null;
    }

    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

// Prevent duplicate custom element registration.
if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
